import React, {useState, useEffect, useRef} from "react";
import {Tooltip} from 'react-tooltip';
import {AiOutlineQuestionCircle} from "react-icons/ai";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";


const OrderDetails = ({
                          numPassengers,
                          setNumPassengers,
                          quotation,
                          passengers,
                          MAX_NUM_PASSENGERS = 1,
                          isFormValid,
                          handleSubmitNextStep,
                          nextStepLabel,
                          handleCheckboxChange,
                          showTerms = false,
                          isReservation = false,
                          isPending = false,
                          payment = {},
                          selectDate = "",
                          haveTime= false,
                          selectedOption = ""
                      }) => {
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const initialPassengersWithFullName = useRef(0);

    dayjs.extend(customParseFormat);
    dayjs.extend(utc)

    // Add effect to handle input focus events
    useEffect(() => {
        const handleFocusIn = () => {
            // Only set keyboard visible on mobile devices
            if (window.innerWidth <= 768) {
                setIsKeyboardVisible(true);
            }
        };

        const handleFocusOut = () => {
            setIsKeyboardVisible(false);
        };

        // Listen for focus events on the document
        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('focusout', handleFocusOut);

        return () => {
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('focusout', handleFocusOut);
        };
    }, []);

    // Effect to calculate the initial number of passengers with fullName
    useEffect(() => {
        // If it's the first run, calculate the initial valid passengers
        if (initialPassengersWithFullName.current === 0) {
            const validPassengers = passengers.filter(
                (passenger) => passenger.fullName && passenger.idNumber
            ).length;
            initialPassengersWithFullName.current = validPassengers;
        }
    }, [passengers]); // Dependency on passengers ensures this runs when passengers change

    const handleAddPassenger = () => {
        const maxPassengers = Math.min(
            initialPassengersWithFullName.current + quotation.stock,
            MAX_NUM_PASSENGERS
        );
        if (numPassengers < maxPassengers) {
            setNumPassengers((prev) => prev + 1);
        }
    };

    const handleRemovePassenger = () => {
        if (numPassengers > 1) {
            setNumPassengers((prev) => prev - 1);
        }
    };

    useEffect(() => {
        setIsSmallScreen(window.innerWidth <= 768);

        const handleResize = () => {
            setIsSmallScreen(window.innerWidth <= 768);
        };
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [isSmallScreen]);

    return (
        <div className={`order-details__container ${isKeyboardVisible ? 'keyboard-visible' : ''}`}>
            {!isSmallScreen && (
                <div
                    className="order-price"
                >
                    <div className="h2">
                        <h2>
                            {quotation.currency.code} {quotation.total}
                        </h2>
                        <span>por persona</span>
                    </div>
                </div>
            )}
            {setNumPassengers && passengers && passengers[0] && (<div className="num-people__container">
                <span>Cantidad de pasajeros</span>
                <div className="num-people__actions">
                    {setNumPassengers && (
                        <button onClick={handleRemovePassenger}>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_225_460)">
                                    <path
                                        d="M6 12H18"
                                        stroke="#83868C"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_225_460">
                                        <rect width="24" height="24" fill="white"/>
                                    </clipPath>
                                </defs>
                            </svg>
                        </button>
                    )}
                    <span style={!setNumPassengers ? {marginRight: "3rem"} : {}}>
                        {numPassengers}
                    </span>
                    {setNumPassengers && (
                        <button onClick={handleAddPassenger}>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_225_466)">
                                    <path
                                        d="M12 12V18M6 12H12H6ZM18 12H12H18ZM12 12V6V12Z"
                                        stroke="#1B1C1E"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_225_466">
                                        <rect width="24" height="24" fill="white"/>
                                    </clipPath>
                                </defs>
                            </svg>
                        </button>
                    )}
                </div>
            </div>)}

            {!setNumPassengers && passengers && passengers[0] && (
                <div className="order-passengers__container">
                    {passengers.map((passenger, index) => {
                        return (
                            <div key={index}>
                                #{index + 1} {passenger.full_name}{" "}
                            </div>
                        );
                    })}
                </div>
            )}
            {selectDate && (
                <div className="order-selectedDate__container">
                    <span>Fecha seleccionada:</span>

                    {haveTime ? (
                        <span className="order-selectedDate__container--date">{dayjs.utc(selectDate).format('DD/MM/YY HH:mm').toString()}</span>
                    ): (
                        <span className="order-selectedDate__container--date">{dayjs.utc(selectDate).format('DD/MM/YY').toString()}</span>
                    )}

                </div>
            )}
            {selectedOption && (
                <div className="order-selectedOption__container">
                    <span>Opción seleccionada: </span>
                    <span
                        className="order-selectedOption__container--option">{selectedOption}</span>
                </div>
            )}
            {!isSmallScreen && (
                <div className="order-description__container">
                    <h3>{quotation.title}</h3>
                    <p dangerouslySetInnerHTML={{ __html: quotation.itinerary.description }} />
                </div>
            )}

            {isReservation && (
                <div className="reservation__container">
                    <div className="reservation__container__total">
                        <div className="total-text">
                            <h3>Total</h3>
                        </div>
                        <div className="total-price">
                            <h3>{quotation.currency.code} {numPassengers * quotation.total}</h3>
                        </div>
                    </div>
                    <div className="reservation__container__reservation">
                        <div className="reservation-text">
                            <h2>A pagar ahora</h2> <AiOutlineQuestionCircle className="due_now_tooltip_btn"/>
                            <Tooltip
                                anchorSelect=".due_now_tooltip_btn"
                                className="due_now_tooltip"
                                place="bottom"
                            >
                                <p> Asegura tu lugar con un pago <span> no reembolsable </span>
                                        de <span>{quotation.currency.code} {numPassengers * quotation.min_amount}</span>.<br/><br/>
                                        Recibirás la información para pagar el saldo restante en el correo de
                                        confirmación.</p>
                            </Tooltip>
                        </div>
                        <div className="reservation-price">
                            <h2>{quotation.currency.code} {numPassengers * quotation.min_amount}</h2>
                        </div>
                    </div>
                </div>
            )}
            {!isReservation && (
                <div className="total__container">
                    <div className="total-text">
                        {!isPending && (<h2>Total</h2>)}
                        {isPending && (<h2>Saldo a pagar</h2>)}
                    </div>
                    <div className="total-price">
                        {!isPending && (<h2>{quotation.currency.code} {numPassengers * quotation.total}</h2>)}
                        {isPending && (<h2>{quotation.currency.code} {payment.pending_amount}</h2>)}
                        <span>Todos los impuestos y cargos incluidos</span>
                    </div>
                </div>
            )}
            {isSmallScreen && nextStepLabel && (
                <div className="checkout-actions checkout-actions--mobile">
                    {showTerms && (
                        <div className="checkbox">
                            <input type="checkbox" onChange={handleCheckboxChange}></input>
                            <label>
                                {/* eslint-disable-next-line */}
                                Acepto <a href="https://trippromoter.com/terminos-y-condiciones/" rel="noreferrer"
                                          target="_blank">los términos y condiciones</a>
                            </label>
                        </div>
                    )}
                    <button type="button" onClick={handleSubmitNextStep} disabled={!isFormValid}>
                        {nextStepLabel}
                    </button>
                </div>
            )}
        </div>
    );
};

export default OrderDetails;
